"use client";

import { DashboardHeader } from "@/components/layout/dashboard-header";
import {
  MobileBottomNavigation,
  MobileTopBar,
} from "@/components/layout/mobile-navigation";
import { RightSidebar } from "@/components/layout/right-sidebar";
import { SidebarNav } from "@/components/layout/sidebar-nav";
import { SidebarStateProvider, useSidebarState } from "@/components/layout/sidebar-state";
import { FirstUseTour } from "@/components/onboarding/first-use-tour";
import { IntroductionProgressTracker } from "@/components/onboarding/introduction-progress-tracker";
import { VideoCallProvider } from "@/components/chat/video-call-provider";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

type DashboardLayoutProps = {
  children: React.ReactNode;
  initialSidebarCollapsed?: boolean;
};

function DashboardShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { collapsed } = useSidebarState();
  const pagesWithoutRightSidebar = [
    "/chat",
    "/services",
    "/notifications",
    "/practices",
    "/settings",
  ];
  const showRight = !pagesWithoutRightSidebar.some((path) =>
    pathname.startsWith(path),
  );
  const isChat = pathname.startsWith("/chat");

  return (
    <div className="min-h-[100dvh] w-full max-w-full overflow-x-clip bg-background">
      <FirstUseTour />
      <IntroductionProgressTracker />
      <MobileTopBar />
      <DashboardHeader />

      <div className="flex w-full">
        <aside
          className={cn(
            "sticky top-16 left-0 hidden h-[calc(100vh-4rem)] shrink-0 border-r border-black/[0.04] bg-sidebar transition-[width] duration-200 md:block",
            collapsed ? "z-30 w-[76px] overflow-visible" : "w-[248px]",
          )}
        >
          <SidebarNav />
        </aside>

        <main
          className={cn(
            "min-w-0 flex-1",
            isChat
              ? "p-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0"
              : "px-0 pt-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-5 md:px-10 md:py-6 xl:px-12",
          )}
        >
          {children}
        </main>

        {showRight ? (
          <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-[312px] shrink-0 overflow-hidden xl:block">
            <RightSidebar />
          </aside>
        ) : null}
      </div>
      <MobileBottomNavigation />
    </div>
  );
}

export function DashboardLayout({
  children,
  initialSidebarCollapsed = false,
}: DashboardLayoutProps) {
  return (
    <SidebarStateProvider initialCollapsed={initialSidebarCollapsed}>
      <VideoCallProvider>
        <DashboardShell>{children}</DashboardShell>
      </VideoCallProvider>
    </SidebarStateProvider>
  );
}
